import { NAV_GROUPS, PATHS, type NavItemId } from '@ctcells/core'
import { useCallback, useState } from 'react'

/**
 * 통합 콘솔 작업 줄의 **접힘 · 펼침** — 메뉴마다 기본값이 있다(eric 2026-10-03 「다른 메뉴로 갈 때는 상시창이 줄어든 상태
 * — 다른 메뉴는 줄인 상태, 모니터링 페이지는 펼친 상태로 보이게 해줘」).
 *
 *   모니터링         → 펼침
 *   그 밖의 메뉴 전부 → 접힘(작업 관리 · 실험 준비 · 알람 · 관리 · 내 계정 · 없는 주소)
 *
 * - 접기/펼치기 단추를 누르면 **그 메뉴에 있는 동안은** 사람이 고른 쪽을 따른다(메뉴 안의 탭 · 상세 서랍으로 옮겨도 그대로).
 * - 🔴 **메뉴를 옮기면 그 메뉴의 기본값으로 다시 선다.** 모니터링에서 접고 작업 관리에 갔다 돌아오면 다시 펼침이다.
 * - 🔴 **브라우저 저장소에 남기지 않는다.** 종전(2026-10-01 · `useStripFoldStore`)에는 한 번 고른 값이 전 화면 · 새로고침에
 *   이어졌는데, 메뉴별 기본값과 부딪혀서 걷었다. 새로고침은 그 메뉴의 기본값이다.
 * - «메뉴»는 헤더의 문 하나다(`NAV_GROUPS` 의 항목 — `path` + `also`). 헤더 불과 같은 표라 「작업」 문의 이력 탭도
 *   같은 메뉴다(`also: [PATHS.batchHistory]`). 헤더 밖 경로는 첫 마디가 메뉴다.
 */

const MONITORING_MENU: NavItemId = 'monitoring'

const under = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`)

/** 메뉴 문 하나 = 그 문이 쥔 경로들 */
const MENU_PATHS: readonly { id: string; paths: readonly string[] }[] = NAV_GROUPS.flatMap(
  (group) => group.items,
).map((item) => ({
  id: item.id,
  paths: [item.path, ...(item.also ?? [])],
}))

/** 지금 경로가 속한 메뉴. 헤더 문에 안 걸리면(내 계정 · 없는 주소) 경로의 첫 마디 */
export function stripMenuOf(pathname: string): string {
  const hit = MENU_PATHS.find((menu) => menu.paths.some((path) => under(pathname, path)))
  if (hit !== undefined) return hit.id
  return pathname.split('/')[1] ?? ''
}

/**
 * 시작이 받아들여진 뒤 갈 곳(eric 2026-10-26 「작업을 선택하고 시작을 누르면 모니터링 화면으로 가게 해줘」) — 모니터링 메뉴 밖이면
 * 모니터링, 이미 모니터링 메뉴(하위 탭 포함)면 `undefined`(그대로 — 보던 탭을 바꾸지 않는다). 메뉴 판정은 접힘과 같은 표다.
 */
export function landingAfterStart(pathname: string): string | undefined {
  return stripMenuOf(pathname) === MONITORING_MENU ? undefined : PATHS.monitoring
}

/** 메뉴의 기본값 — 모니터링만 펼침이다 */
export function stripCollapsedByDefault(menu: string): boolean {
  return menu !== MONITORING_MENU
}

export interface StripFold {
  collapsed: boolean
  toggle: () => void
}

export function useStripFold(pathname: string): StripFold {
  const menu = stripMenuOf(pathname)
  const [fold, setFold] = useState(() => ({ menu, collapsed: stripCollapsedByDefault(menu) }))
  /*
   * 메뉴가 바뀌면 그 메뉴의 기본값으로 되돌린다 — 렌더 중에 «앞 렌더의 메뉴»와 대조하는 React 의 정석 꼴
   * (https://react.dev/reference/react/useState#storing-information-from-previous-renders). 효과로 하면 한 프레임
   * 옛 값이 그려진다.
   */
  if (fold.menu !== menu) setFold({ menu, collapsed: stripCollapsedByDefault(menu) })
  const collapsed = fold.menu === menu ? fold.collapsed : stripCollapsedByDefault(menu)
  const toggle = useCallback(() => {
    setFold((current) => ({ ...current, collapsed: !current.collapsed }))
  }, [])
  return { collapsed, toggle }
}
